import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, inputCls } from "@/components/Peep";
import { MediaImage } from "@/components/Media";
import { ProfileImagePick } from "@/components/ProfileImagePick";

function Preview({ c, headline }) {
  return (
    <div data-testid="promo-preview" className="grid gap-4 md:grid-cols-[1fr_180px]">
      <div className="relative h-40 overflow-hidden rounded-2xl border border-[#6a00ff]/30">
        {c.banner_url ? <MediaImage url={c.banner_url} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-[#2a0f3d] to-[#080808]" />}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-[#1a0626]/50 to-transparent" />
        <div className="absolute left-5 top-1/2 -translate-y-1/2">
          <p className="text-[0.6rem] tracking-[0.35em] text-white/70">FEATURED CREATOR</p>
          <p className="mt-1 text-sm font-light uppercase tracking-[0.2em]">{headline || "Your headline"}</p>
          <p className="bg-gradient-to-r from-[#b04bff] to-[#d42be8] bg-clip-text text-2xl font-black uppercase text-transparent">{c.display_name}</p>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-[#6a00ff]/30 bg-[#0f0a15]">
        <div className="h-28">{(c.card_url || c.avatar_url) ? <MediaImage url={c.card_url || c.avatar_url} className="h-full w-full object-cover" /> : <div className="h-full bg-[#1a0f24]" />}</div>
        <p className="flex items-center gap-1 px-3 pt-2 text-sm font-bold">{c.display_name} <BadgeCheck size={14} className="text-[#4aa3ff]" /></p>
        <p className="px-3 pb-3 text-[0.65rem] text-[#a7a0af]">@{c.handle}</p>
      </div>
    </div>
  );
}

const PRESET = ["Lifestyle", "Fashion", "Fitness", "Beauty", "Behind the Scenes", "Entertainment"];

function Categories({ value, onSave }) {
  const [sel, setSel] = useState(value);
  const [custom, setCustom] = useState("");
  useEffect(() => { setSel(value); }, [value]);
  const full = () => { toast.error("You can choose up to 6 categories. Remove one first."); };
  const toggle = (x) => { if (sel.includes(x)) setSel(sel.filter((y) => y !== x)); else if (sel.length >= 6) full(); else setSel([...sel, x]); };
  const add = () => { const x = custom.trim(); if (sel.length >= 6) full(); else if (x.length >= 2 && !sel.includes(x)) setSel([...sel, x]); setCustom(""); };
  const all = [...PRESET, ...sel.filter((x) => !PRESET.includes(x))];
  return (
    <div data-testid="promo-categories" className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {all.map((x) => (
          <button key={x} type="button" onClick={() => toggle(x)} data-testid={`promo-cat-${x.toLowerCase().replace(/\s/g, "-")}`}
            className={`rounded-full border px-3 py-1.5 text-xs ${sel.includes(x) ? "border-[#a03cff] bg-[#3a0d6e]/60" : "border-white/15"}`}>{x}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input maxLength={24} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Add your own category" data-testid="promo-cat-custom" className={`${inputCls} flex-1`} />
        <button type="button" onClick={add} data-testid="promo-cat-add" className="rounded-xl border border-white/15 px-4 text-sm">Add</button>
      </div>
      <p className="text-[0.65rem] text-[#6f6878]">Pick up to 6. Subscribers can filter discovery by these categories. ({sel.length}/6)</p>
      <button type="button" onClick={() => onSave(sel)} data-testid="promo-cat-save" className="btn-brand w-fit rounded-xl px-5 py-2.5 text-sm font-semibold">Save categories</button>
    </div>
  );
}

export default function Promotion() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ promo_headline: "", bio: "" });
  const load = useCallback(() => {
    api.get("/creator/profile").then((r) => {
      setC(r.data.creator);
      setForm({ promo_headline: r.data.creator.promo_headline || "", bio: r.data.creator.bio || "" });
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (extra = {}, msg = "Saved") => {
    try {
      await api.put("/creator/profile", { ...form, ...extra });
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const saveImage = (key) => (id) => api.put("/creator/profile", { [key]: id });
  if (!c) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  const live = !!c.promo_published;
  return (
    <div data-testid="creator-promotion" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Discovery & Promotion</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Control how you appear on the PEEPING discovery dashboard. These images are public promotional assets — never upload paid content here.</p>
      </div>
      <Panel title="Status" action={<span data-testid="promo-status" className={`text-xs font-semibold ${live ? "text-emerald-300" : "text-[#a7a0af]"}`}>{live ? "Published on discovery" : "Not published"}</span>}>
        <div className="flex flex-wrap items-center gap-3">
          {live ? (
            <button onClick={() => save({ promo_published: false }, "Promotion unpublished")} data-testid="promo-unpublish" className="flex items-center gap-1 rounded-xl border border-white/15 px-4 py-2 text-sm"><EyeOff size={14} /> Unpublish</button>
          ) : (
            <button onClick={() => save({ promo_published: true }, "Published to discovery")} data-testid="promo-publish" className="btn-brand flex items-center gap-1 rounded-xl px-4 py-2 text-sm font-semibold"><Eye size={14} /> Publish to discovery</button>
          )}
          <p className="text-xs text-[#6f6878]">Published banners join the homepage carousel automatically and rotate fairly with other creators.</p>
        </div>
      </Panel>
      <Panel title="Images">
        <div className="grid gap-4 md:grid-cols-3">
          <ProfileImagePick label="Profile photo" hint="Square, at least 400×400" url={c.avatar_url} save={saveImage("avatar_asset_id")} onSaved={load} testid="promo-avatar-input" />
          <ProfileImagePick label="Discovery card image" hint="Portrait 4:5, at least 600×750" url={c.card_url} save={saveImage("card_asset_id")} onSaved={load} testid="promo-card-input" />
          <ProfileImagePick label="Discovery hero banner" hint="Wide 16:5, at least 1600×500 · also shown at the top of your public page if you haven't set a profile banner" url={c.banner_url} save={saveImage("banner_asset_id")} onSaved={load} testid="promo-banner-input" />
        </div>
      </Panel>
      <Panel title="Text">
        <div className="grid gap-3">
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Promotional headline (80 characters)
            <input maxLength={80} className={inputCls} data-testid="promo-headline" value={form.promo_headline} onChange={(e) => setForm({ ...form, promo_headline: e.target.value })} placeholder="e.g. A more personal world" /></label>
          <label className="flex flex-col gap-1 text-xs text-[#a7a0af]">Public biography (500 characters)
            <textarea maxLength={500} rows={3} className={inputCls} data-testid="promo-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></label>
          <button onClick={() => save()} data-testid="promo-save" className="btn-brand w-fit justify-self-start rounded-xl px-5 py-2.5 text-sm font-semibold">Save text</button>
        </div>
      </Panel>
      <Panel title="Discovery categories">
        <Categories value={c.categories || []} onSave={(categories) => save({ categories }, "Categories saved")} />
      </Panel>
      <Panel title="Preview">
        <Preview c={c} headline={form.promo_headline} />
        <p className="mt-3 text-xs text-[#a7a0af]">Public previews: mark posts as <span className="text-white">Preview</span> access in <Link to="/creator/content" className="text-[#f0a8fa] underline">Content</Link> to let non-members see them.</p>
      </Panel>
    </div>
  );
}
